import { useQuery } from '@tanstack/react-query';
import type { OfficerPositionView, TrackHistory, TrackedOfficer } from '@munifocus/contracts-gov';
import { api } from './api-client';

/**
 * The supervisor's live team map and history replay. Every read is scoped to
 * one municipality and gated server-side on `tracking:view` there.
 */
export const trackingKeys = {
  live: (localGovernmentId: string) => ['tracking', 'live', localGovernmentId] as const,
  tracked: (localGovernmentId: string) => ['tracking', 'tracked', localGovernmentId] as const,
  track: (localGovernmentId: string, officerId: string, date: string) =>
    ['tracking', 'track', localGovernmentId, officerId, date] as const,
};

export function useLiveOfficers(localGovernmentId: string | undefined) {
  return useQuery({
    queryKey: trackingKeys.live(localGovernmentId ?? ''),
    queryFn: async () => {
      const { data } = await api.get<OfficerPositionView[]>(
        `/tracking/local-governments/${localGovernmentId}/officers`,
      );
      return data;
    },
    enabled: !!localGovernmentId,
    // The socket keeps it current; this is the safety net for a dropped push.
    refetchInterval: 60_000,
  });
}

export function useTrackedOfficers(localGovernmentId: string | undefined) {
  return useQuery({
    queryKey: trackingKeys.tracked(localGovernmentId ?? ''),
    queryFn: async () => {
      const { data } = await api.get<TrackedOfficer[]>(
        `/tracking/local-governments/${localGovernmentId}/history/officers`,
      );
      return data;
    },
    enabled: !!localGovernmentId,
  });
}

export function useTrack(
  localGovernmentId: string | undefined,
  officerId: string | undefined,
  date: string,
) {
  return useQuery({
    queryKey: trackingKeys.track(localGovernmentId ?? '', officerId ?? '', date),
    queryFn: async () => {
      const { data } = await api.get<TrackHistory>(
        `/tracking/local-governments/${localGovernmentId}/officers/${officerId}/track`,
        { params: { date } },
      );
      return data;
    },
    enabled: !!localGovernmentId && !!officerId && /^\d{4}-\d{2}-\d{2}$/.test(date),
  });
}

/** Fold one pushed position into the live list, replacing that officer's marker. */
export function mergeOfficer(
  list: OfficerPositionView[] | undefined,
  officer: OfficerPositionView,
): OfficerPositionView[] {
  return [...(list ?? []).filter((o) => o.officerId !== officer.officerId), officer];
}

/** Seconds since a position was reported. */
export function ageSeconds(recordedAt: string, now: number = Date.now()): number {
  const at = Date.parse(recordedAt);
  return Number.isNaN(at) ? Infinity : Math.max(0, Math.round((now - at) / 1000));
}
